import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  NotificationOutlined,
} from '@ant-design/icons';
import { Form, Input, InputNumber, Popconfirm, Tooltip } from 'antd';
import PropTypes from 'prop-types';
import { createContext, useContext } from 'react';
import styled from 'styled-components';
import { PitstopSelect } from '../PitstopSelect';

const FlexRow = styled.span`
  display: flex;

  .anticon {
    font-size: 1.1rem;
  }
`;

const EditableContext = createContext(null);

const EditableRow = ({ children, ...props }) => {
  const [form] = Form.useForm();
  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}>
        <tr {...props}>{children}</tr>
      </EditableContext.Provider>
    </Form>
  );
};

EditableRow.propTypes = {
  children: PropTypes.any,
};

const EditableCell = ({
  editing,
  dataIndex,
  title,
  inputType,
  record,
  selectOptions,
  required,
  children,
  ...restProps
}) => {
  const form = useContext(EditableContext);

  const getInput = () => {
    if (inputType === 'number') return <InputNumber />;
    if (inputType === 'select')
      return <PitstopSelect options={selectOptions} />;
    return <Input />;
  };

  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item
          name={dataIndex}
          initialValue={record[dataIndex]}
          rules={[{ required, message: `Please Input ${title}!` }]}
          style={{ margin: 0 }}
        >
          {getInput()}
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};

EditableCell.propTypes = {
  children: PropTypes.any,
  required: PropTypes.bool,
  inputType: PropTypes.string,
  title: PropTypes.string,
  selectOptions: PropTypes.any,
};

export const editableGridComponents = {
  body: {
    row: EditableRow,
    cell: EditableCell,
  },
};

export const getEditableGridActionColumn = ({
  isEditing,
  onCancel,
  onDelete,
  onEdit,
  onSave,
  hasNotification,
  onClickNotification,
}) => ({
  title: '',
  dataIndex: 'editAction',

  render: (text, record) => {
    const editing = isEditing(record);
    return (
      <EditableGridActionsPanel
        editing={editing}
        onCancel={onCancel}
        onDelete={onDelete}
        onEdit={onEdit}
        onSave={onSave}
        record={record}
        hasNotification={hasNotification}
        onClickNotification={onClickNotification}
      />
    );
  },
});

export function EditableGridActionsPanel({
  editing,
  onCancel,
  onDelete,
  onEdit,
  onSave,
  record,
  hasNotification,
  onClickNotification,
}) {
  const form = useContext(EditableContext);

  return (
    <div>
      {editing ? (
        <span>
          <CheckOutlined
            onClick={async () => {
              try {
                const values = await form.validateFields();
                onSave(record, values);
              } catch (error) {
                console.error('Validation Failed:', error);
              }
            }}
            style={{ marginRight: 8 }}
          />
          <Popconfirm title="Sure to cancel?" onConfirm={onCancel}>
            <CloseOutlined />
          </Popconfirm>
        </span>
      ) : (
        <FlexRow>
          {hasNotification && (
            <Tooltip title="Click to edit notifications">
              <NotificationOutlined
                onClick={() => onClickNotification(record)}
                style={{ marginRight: 8 }}
              />
            </Tooltip>
          )}
          <EditOutlined
            onClick={() => onEdit(record)}
            style={{ marginRight: 8 }}
          />
          <Popconfirm
            title="Sure to delete?"
            onConfirm={() => onDelete(record)}
          >
            <DeleteOutlined />
          </Popconfirm>
        </FlexRow>
      )}
    </div>
  );
}
